import React, { useState, useEffect } from 'react';
import { toastSuccess, toastError } from '../../utils/alertHelper';
import axios from '../../utils/axiosInstance';

// Parse API response for role + responsibilities
const parseRoleResponsibilities = (apiResponse) => {
  const responsibilities = apiResponse?.data?.responsibility || [];

  const selectedResponsibilities = [];
  const selectedActions = {};

  responsibilities.forEach((res) => {
    if (res.enabled) selectedResponsibilities.push(res._id);
    selectedActions[res._id] = {};
    (res.actions || []).forEach((action) => {
      selectedActions[res._id][action._id] = !!action.enabled;
    });
  });

  return { responsibilities, selectedResponsibilities, selectedActions };
};

function Responsibility() {
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [responsibilities, setResponsibilities] = useState([]);
  const [selectedResponsibilities, setSelectedResponsibilities] = useState([]);
  const [selectedActions, setSelectedActions] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [saving, setSaving] = useState(false);

  // Load roles + default role responsibilities
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const res = await axios.get('/admin/api/role');
        const roleList = res.data?.roles || [];
        setRoles(roleList);

        if (roleList.length) {
          const firstRoleId = roleList[0]._id;
          setSelectedRole(firstRoleId);
          fetchRoleResponsibilities(firstRoleId);
        }
      } catch {
        toastError('Failed to load roles');
      }
    };
    fetchRoles();
  }, []);

  // Fetch responsibilities for selected role
  const fetchRoleResponsibilities = async (roleId) => {
    try {
      const res = await axios.get(
        `/admin/api/role/roleby-responsibility/${roleId}`
      );
      const {
        responsibilities,
        selectedResponsibilities,
        selectedActions,
      } = parseRoleResponsibilities(res.data);

      setResponsibilities(responsibilities);
      setSelectedResponsibilities(selectedResponsibilities);
      setSelectedActions(selectedActions);
    } catch {
      toastError('Failed to load role responsibilities');
    }
  };

  // Toggle responsibility
  const handleCheckboxChange = (responsibility, isChecked) => {
    setSelectedResponsibilities((prev) => {
      const set = new Set(prev);
      if (isChecked) {
        set.add(responsibility._id);
        setSelectedActions((prevActions) => {
          const current = { ...prevActions };
          if (!current[responsibility._id]) {
            current[responsibility._id] = {};
          }
          return current;
        });
      } else {
        set.delete(responsibility._id);
        setSelectedActions((prevActions) => {
          const current = { ...prevActions };
          delete current[responsibility._id];
          return current;
        });
      }
      return Array.from(set);
    });
  };

  // Toggle action
  const handleActionChange = (resId, actionId, isChecked) => {
    setSelectedActions((prev) => {
      const resActions = prev[resId] || {};
      return {
        ...prev,
        [resId]: {
          ...resActions,
          [actionId]: isChecked,
        },
      };
    });
  };

  // Save responsibilities
  const handleSaveResponsibilities = async () => {
    if (!selectedRole) {
      toastError('Please select a role first');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        roleId: selectedRole,
        responsibilities: selectedResponsibilities.map((resId) => {
          const enabledActionIds = Object.entries(selectedActions[resId] || {})
            .filter(([_, enabled]) => enabled)
            .map(([actionId]) => ({ action: actionId }));
          return { responsibility: resId, actions: enabledActionIds };
        }),
      };

      // 1. Save API
      await axios.post('/admin/api/role/save-responsibilities', payload);
      toastSuccess('Responsibilities assigned successfully!');

      // 2. Refresh with updated data
      await fetchRoleResponsibilities(selectedRole);
    } catch (err) {
      console.error('Failed to save responsibilities:', err);
      toastError('Failed to save responsibilities');
    } finally {
      setSaving(false);
    }
  };

  // Filter responsibilities by search
  const filteredResponsibilities = responsibilities.filter((item) =>
    item.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const isResponsibilityAssigned = (responsibility) =>
    selectedResponsibilities.includes(responsibility._id);

  return (
    <div className="container mt-2">
      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center p-2">
          <h5 className="mb-0">Responsibility</h5>
        </div>
        
          <div className="d-flex justify-content-between align-items-center p-1 border-bottom">
            <select
              className="form-select w-25"
              value={selectedRole}
              onChange={(e) => {
                setSelectedRole(e.target.value);
                fetchRoleResponsibilities(e.target.value);
              }}
            >
            
              {roles.map((role) => (
                <option key={role._id} value={role._id}>
                  {role.name}
                </option>
              ))}
            </select>

            <div className="d-flex gap-2">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ width: '200px' }}
              />
              <button
                className="btn btn-sm btn-success"
                onClick={handleSaveResponsibilities}
                disabled={!selectedRole || saving}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
         <div className="table-responsive">
          <div className="card-body p-0">
          <table className="table table-bordered table-sm mb-0 fs-6">
            <thead className="table-light text-center">
              <tr>
                <th className="px-3">Module Name</th>
                <th className="px-3">Sub Module</th>
                <th className="px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredResponsibilities.length > 0 ? (
                filteredResponsibilities.map((item) => (
                  <tr key={item._id}>
                    <td>
                      <div className="d-flex align-items-center">
                        <input
                          type="checkbox"
                          className="form-check-input ml-0"
                          checked={isResponsibilityAssigned(item)}
                          onChange={(e) =>
                            handleCheckboxChange(item, e.target.checked)
                          }
                          disabled={!selectedRole}
                        />
                        <span className="ml-4">{item.name}</span>
                      </div>
                    </td>
                    <td></td>
                    <td className="text-center">
                      <div className="d-flex flex-column align-items-center gap-1">
                        {(item.actions || []).map((action) => (
                          <div className="form-check" key={action._id}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id={`${item._id}_${action._id}`}
                              checked={
                                selectedActions[item._id]?.[action._id] || false
                              }
                              onChange={(e) =>
                                handleActionChange(
                                  item._id,
                                  action._id,
                                  e.target.checked
                                )
                              }
                              disabled={
                                !selectedRole ||
                                !isResponsibilityAssigned(item)
                              }
                            />
                            <label
                              className="form-check-label ms-2"
                              htmlFor={`${item._id}_${action._id}`}
                            >
                              {action.name}
                            </label>
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className="text-center py-3 text-muted">
                    No responsibilities found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
  );
}

export default Responsibility;
